/**
 * Admin > Quote Presets: the account's quote presets (quote_template_names)
 * and their categories (quote_template_category), the port of legacy
 * admin/admin_quote_template_list.php. QuotePresetsAdminController:
 *   /admin/quote-presets             list (deleted ones too), create, update
 *                                    (partial: name, category, active and the
 *                                    content), delete (active = 0)
 *   /admin/quote-presets/hour-bands  the Hours choices for a preset's
 *                                    procedures (it has no facility)
 *   /admin/quote-preset-categories   list (with deleted), create, update
 *                                    (name, active), delete (only once empty)
 *
 * A preset's content — the procedures, products and discounts Load Preset
 * copies onto a patient's quote — lives on the preset's own quote, read with
 * getQuote and saved with the preset.
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';
import type { QuoteDiscount, QuoteItem, QuoteProcedure } from './quotes.service';

export interface QuotePreset {
  quoteTemplateId: number;
  accountId: number;
  name: string;
  /** The quote holding its content; null when it has none yet. */
  quoteId: number | null;
  active: boolean;
  quoteTemplateCategoryId: number | null;
  order: number;
}

export interface QuotePresetCategory {
  categoryId: number;
  name: string;
  active: boolean;
  order: number;
}

/** What a preset loads onto a quote. */
export interface QuotePresetContent {
  procedures: Partial<QuoteProcedure>[];
  items: Partial<QuoteItem>[];
  discounts: Partial<QuoteDiscount>[];
}

export function listQuotePresets(): Promise<QuotePreset[]> {
  return apiClient.get<ApiResponse<QuotePreset[]>>('/admin/quote-presets').then((r) => r.data.data ?? []);
}

export function getQuotePresetHourBands(): Promise<number[]> {
  return apiClient.get<ApiResponse<number[]>>('/admin/quote-presets/hour-bands').then((r) => r.data.data ?? []);
}

export function createQuotePreset(
  data: { name: string; quoteTemplateCategoryId: number } & QuotePresetContent,
): Promise<QuotePreset> {
  return apiClient.post<ApiResponse<QuotePreset>>('/admin/quote-presets', data).then((r) => r.data.data);
}

/** Partial: a key left out is left as stored (a content key too). */
export function updateQuotePreset(
  id: number,
  data: Partial<{ name: string; quoteTemplateCategoryId: number; active: boolean } & QuotePresetContent>,
): Promise<QuotePreset> {
  return apiClient.put<ApiResponse<QuotePreset>>(`/admin/quote-presets/${id}`, data).then((r) => r.data.data);
}

export function deleteQuotePreset(id: number): Promise<void> {
  return apiClient.delete(`/admin/quote-presets/${id}`).then(() => undefined);
}

export function restoreQuotePreset(id: number): Promise<QuotePreset> {
  return updateQuotePreset(id, { active: true });
}

export function listQuotePresetCategories(): Promise<QuotePresetCategory[]> {
  return apiClient
    .get<ApiResponse<QuotePresetCategory[]>>('/admin/quote-preset-categories')
    .then((r) => r.data.data ?? []);
}

export function createQuotePresetCategory(name: string): Promise<QuotePresetCategory> {
  return apiClient
    .post<ApiResponse<QuotePresetCategory>>('/admin/quote-preset-categories', { name })
    .then((r) => r.data.data);
}

export function updateQuotePresetCategory(
  id: number,
  data: Partial<{ name: string; active: boolean }>,
): Promise<QuotePresetCategory> {
  return apiClient
    .put<ApiResponse<QuotePresetCategory>>(`/admin/quote-preset-categories/${id}`, data)
    .then((r) => r.data.data);
}

/** Refused (422) while the category holds a live preset. */
export function deleteQuotePresetCategory(id: number): Promise<void> {
  return apiClient.delete(`/admin/quote-preset-categories/${id}`).then(() => undefined);
}

export function restoreQuotePresetCategory(id: number): Promise<QuotePresetCategory> {
  return updateQuotePresetCategory(id, { active: true });
}
